import { createEmitter } from './events';
import { bootstrap as bootstrapJwt } from './bootstrap-redirect';
import { createUsageQueue, type UsageQueue, type ClientEvent } from './usage/queue';
import { startVisibilityTracking } from './usage/visibility';
import { wrapGemini as _wrapGemini, wrapOpenAI as _wrapOpenAI, wrapAnthropic as _wrapAnthropic } from './usage/wrappers';
import { computeCost } from './usage/pricing';
import type { SidekickConfig, SidekickUser, SidekickEvent, JwtPayload } from './types';

const emitter = createEmitter<{ logout: []; revoked: []; disconnected: [] }>();

// Capture the origin of the script tag that loaded this SDK.
// MUST run at IIFE init time (top-level), NOT later inside init(): when init()
// runs from an inline `async function`, `document.currentScript` is null,
// and `document.scripts.find(s => s.src.endsWith('/sdk.js'))` may miss the
// script tag in apps that inject it via Next.js <Script strategy="..."/>,
// or otherwise mutate <head> at hydration time. Capturing now bypasses both.
const SDK_LOAD_ORIGIN: string | null = (() => {
  try {
    if (typeof document === 'undefined') return null;
    const cs = document.currentScript as HTMLScriptElement | null;
    if (cs?.src) return new URL(cs.src).origin;
    // Fallback at load: search synchronously while DOM has whatever scripts
    // are present right now (still useful when SDK is bundled with the app).
    const tag = Array.from(document.scripts).find((s) => s.src.endsWith('/sdk.js'));
    if (tag?.src) return new URL(tag.src).origin;
  } catch { /* swallow */ }
  return null;
})();

let currentUser: SidekickUser | null = null;
let currentJwt: string | null = null;
let sidekickOrigin = '';
let backendUrl = '';
let usageQueue: UsageQueue | null = null;

function decodeJwtPayload(jwt: string): JwtPayload {
  try {
    const [, payloadB64] = jwt.split('.');
    if (!payloadB64) throw new Error('missing payload segment');
    const normalized = payloadB64.replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(normalized)) as JwtPayload;
  } catch (e) {
    throw new Error('sidekick: JWT payload is malformed');
  }
}

// Resolve the Sidekick origin used for /authorize redirects. Look-up order:
//   1. cfg.sidekickOrigin (explicit, preferred for production)
//   2. <meta name="sidekick-origin" content="https://...">
//   3. SDK_LOAD_ORIGIN — captured at IIFE init when currentScript is reliable
//   4. Last-resort: search document.scripts at call time (legacy path)
function resolveSidekickOrigin(cfg: SidekickConfig): string {
  if (cfg.sidekickOrigin) return cfg.sidekickOrigin;
  const meta = document.querySelector('meta[name="sidekick-origin"]') as HTMLMetaElement | null;
  if (meta?.content) return meta.content;
  if (SDK_LOAD_ORIGIN) return SDK_LOAD_ORIGIN;
  const script = Array.from(document.scripts).find((s) => s.src.endsWith('/sdk.js'));
  if (script?.src) return new URL(script.src).origin;
  throw new Error('sidekick: cannot resolve sidekickOrigin (pass it explicitly in init())');
}

const api = {
  async init(cfg: SidekickConfig): Promise<void> {
    sidekickOrigin = resolveSidekickOrigin(cfg);
    backendUrl = cfg.backendUrl ?? sidekickOrigin;

    // Bootstrap: read JWT from fragment / sessionStorage, or redirect to /authorize.
    let token: { jwt: string; exp: number };
    try {
      token = bootstrapJwt({ sidekickOrigin, appId: cfg.appId });
    } catch {
      // bootstrap navigated away; this Promise never resolves.
      return new Promise<void>(() => { /* hang forever */ });
    }

    currentJwt = token.jwt;
    const payload = decodeJwtPayload(token.jwt);
    currentUser = {
      id: payload.sub, email: payload.email, name: payload.name,
      company: payload.company, department: payload.department, role: payload.role,
    };

    // When the JWT expires, redirect back through /authorize so the user
    // either keeps going (cookie still valid) or sees the login page.
    const msUntilExpiry = Math.max(0, (token.exp * 1000) - Date.now() - 5000);
    setTimeout(() => {
      location.assign(`${sidekickOrigin}/authorize?app=${encodeURIComponent(cfg.appId)}`);
    }, msUntilExpiry);

    usageQueue = createUsageQueue({
      url: `${backendUrl}/usage/ingest`,
      getToken: () => currentJwt,
      flushIntervalMs: 30_000,
      batchMax: 20,
    });
    startVisibilityTracking({
      enqueue: (e) => usageQueue!.enqueue(e),
      flushBeacon: () => usageQueue!.flushBeacon(),
      heartbeatIntervalMs: 60_000,
    });
  },
  get user() { return currentUser; },
  isAuthenticated() { return currentUser != null && currentJwt != null; },
  getToken() { return currentJwt; },
  on(ev: SidekickEvent, cb: () => void) {
    console.warn(
      `[sidekick] Event '${ev}' is no longer emitted in SDK v2. The redirect-based ` +
      `auth flow does not fire client-side events for session changes. ` +
      `Listen for HTTP 401 from your own API calls instead.`
    );
    emitter.on(ev, cb);
  },
  off(ev: SidekickEvent, cb: () => void) { emitter.off(ev, cb); },
  trackUsage(event: Omit<Extract<ClientEvent, { type: 'api_call' }>, 'type' | 'occurredAt' | 'costUsd'> & {
    costUsd?: number | null;
  }): void {
    if (!usageQueue) return;
    const occurredAt = Math.floor(Date.now() / 1000);
    const costUsd = event.costUsd
      ?? (event.inputTokens != null && event.outputTokens != null
            ? computeCost(event.model, event.inputTokens, event.outputTokens)
            : null);
    usageQueue.enqueue({ type: 'api_call', occurredAt, ...event, costUsd });
  },
  wrapGemini<T extends { models: { generateContent: any } }>(client: T): T {
    return usageQueue ? _wrapGemini(client, { enqueue: (e) => usageQueue!.enqueue(e) }) : client;
  },
  wrapOpenAI<T extends { chat: { completions: { create: any } } }>(client: T): T {
    return usageQueue ? _wrapOpenAI(client, { enqueue: (e) => usageQueue!.enqueue(e) }) : client;
  },
  wrapAnthropic<T extends { messages: { create: any } }>(client: T): T {
    return usageQueue ? _wrapAnthropic(client, { enqueue: (e) => usageQueue!.enqueue(e) }) : client;
  },
  flush(): Promise<void> {
    return usageQueue ? usageQueue.flush() : Promise.resolve();
  },
};

(globalThis as any).sidekick = api;
export { api as sidekick };
